import React, { useMemo } from 'react';
import { 
  Users, UserCheck, Clock, UserX
} from 'lucide-react';

// Real stats calculation from database data only
const useUserStats = (users, userStats) => {
  return useMemo(() => {
    // If userStats is provided, use it directly
    if (userStats) {
      return {
        totalUsers: userStats.total || 0,
        activeUsers: userStats.active || 0,
        pendingUsers: userStats.pending || 0,
        blockedUsers: userStats.blocked || 0,
        inactiveUsers: userStats.inactive || 0,
        adminUsers: userStats.admin || 0,
        newUsersThisWeek: userStats.newUsersThisWeek || 0
      };
    }

    // Fallback to calculating from users array
    const totalUsers = users?.length || 0;
    const activeUsers = users?.filter(u => u.status === 'active').length || 0;
    const pendingUsers = users?.filter(u => u.status === 'pending').length || 0;
    const blockedUsers = users?.filter(u => u.status === 'blocked').length || 0;
    const inactiveUsers = users?.filter(u => u.status === 'inactive').length || 0;
    const adminUsers = users?.filter(u => u.isAdmin || u.is_admin).length || 0;

    return {
      totalUsers,
      activeUsers,
      pendingUsers,
      blockedUsers,
      inactiveUsers,
      adminUsers,
      newUsersThisWeek: 0 // Only available from backend stats
    };
  }, [users, userStats]);
};

function UserStats({ users, userStats, theme, currentThemeName, onStatClick }) {
  const stats = useUserStats(users, userStats);

  const statCards = [
    {
      id: 'total',
      title: "Total Users",
      value: stats.totalUsers,
      icon: Users,
      color: "#3b82f6",
      gradient: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
      description: "All registered users in the system"
    },
    {
      id: 'active',
      title: "Active Users",
      value: stats.activeUsers,
      icon: UserCheck,
      color: "#10b981",
      gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      description: "Users with active sessions (logged in within 30 days)"
    },
    {
      id: 'pending',
      title: "Pending Approval",
      value: stats.pendingUsers,
      icon: Clock,
      color: "#f59e0b",
      gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
      description: "Users pending verification"
    },
    {
      id: 'blocked',
      title: "Blocked Users",
      value: stats.blockedUsers,
      icon: UserX,
      color: "#ef4444",
      gradient: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
      description: "Users with restricted access"
    },
    {
      id: 'admin',
      title: "Admin Users",
      value: stats.adminUsers,
      icon: Users,
      color: "#8b5cf6",
      gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
      description: "Users with administrative privileges"
    },
    {
      id: 'new',
      title: "New This Week",
      value: stats.newUsersThisWeek,
      icon: Users,
      color: "#06b6d4",
      gradient: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
      description: "Users registered in the last 7 days"
    }
  ];

  return (
    <div 
      className="animate-fade-in-up" 
      style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 24,
        marginBottom: 32,
        position: 'relative',
        zIndex: 1
      }}
      role="region"
      aria-label="User Statistics Overview"
    >
      {statCards.map((stat, index) => (
        <StatCard 
          key={stat.id} 
          {...stat} 
          theme={theme} 
          currentThemeName={currentThemeName}
          onClick={() => onStatClick?.(stat.id)}
          index={index}
        />
      ))}
    </div>
  );
}

function StatCard({ 
  id,
  title, 
  value, 
  icon: Icon, 
  color, 
  gradient,
  description,
  onClick, 
  theme, 
  currentThemeName,
  index 
}) {

  return (
    <div 
      role="button"
      tabIndex={onClick ? 0 : -1}
      aria-label={`${title}: ${value} users. Click to filter by ${title.toLowerCase()}`}
      style={{
        background: currentThemeName === 'dark' 
          ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(15, 23, 42, 0.95) 100%)'
          : currentThemeName === 'beige'
          ? 'linear-gradient(135deg, rgba(245, 245, 220, 0.95) 0%, rgba(250, 235, 215, 0.98) 100%)'
          : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.99) 100%)',
        border: currentThemeName === 'dark'
          ? '1px solid rgba(59, 130, 246, 0.2)'
          : currentThemeName === 'beige'
          ? '1px solid rgba(139, 69, 19, 0.15)'
          : '1px solid rgba(59, 130, 246, 0.1)',
        borderRadius: 20,
        padding: 28,
        boxShadow: currentThemeName === 'dark'
          ? '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(59, 130, 246, 0.1)'
          : currentThemeName === 'beige'
          ? '0 8px 32px rgba(139, 69, 19, 0.1), 0 0 0 1px rgba(139, 69, 19, 0.05)'
          : '0 8px 32px rgba(59, 130, 246, 0.08), 0 0 0 1px rgba(59, 130, 246, 0.05)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        backdropFilter: 'blur(10px)',
        position: 'relative',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        animationDelay: `${index * 0.1}s`,
        outline: 'none'
      }}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
      onMouseEnter={(e) => {
        if (onClick) {
          e.currentTarget.style.transform = 'translateY(-6px) scale(1.02)';
          e.currentTarget.style.boxShadow = currentThemeName === 'dark'
            ? '0 16px 48px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(59, 130, 246, 0.2)'
            : currentThemeName === 'beige'
            ? '0 16px 48px rgba(139, 69, 19, 0.15), 0 0 0 1px rgba(139, 69, 19, 0.1)'
            : '0 16px 48px rgba(59, 130, 246, 0.12), 0 0 0 1px rgba(59, 130, 246, 0.08)';
        }
      }}
      onMouseLeave={(e) => {
        if (onClick) {
          e.currentTarget.style.transform = 'translateY(0) scale(1)';
          e.currentTarget.style.boxShadow = currentThemeName === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(59, 130, 246, 0.1)'
            : currentThemeName === 'beige'
            ? '0 8px 32px rgba(139, 69, 19, 0.1), 0 0 0 1px rgba(139, 69, 19, 0.05)'
            : '0 8px 32px rgba(59, 130, 246, 0.08), 0 0 0 1px rgba(59, 130, 246, 0.05)';
        }
      }}
      onFocus={(e) => {
        if (onClick) {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = currentThemeName === 'dark'
            ? '0 12px 40px rgba(0, 0, 0, 0.4), 0 0 0 2px rgba(59, 130, 246, 0.3)'
            : currentThemeName === 'beige'
            ? '0 12px 40px rgba(139, 69, 19, 0.15), 0 0 0 2px rgba(139, 69, 19, 0.2)'
            : '0 12px 40px rgba(59, 130, 246, 0.12), 0 0 0 2px rgba(59, 130, 246, 0.2)';
        }
      }}
      onBlur={(e) => {
        if (onClick) {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = currentThemeName === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(59, 130, 246, 0.1)'
            : currentThemeName === 'beige'
            ? '0 8px 32px rgba(139, 69, 19, 0.1), 0 0 0 1px rgba(139, 69, 19, 0.05)'
            : '0 8px 32px rgba(59, 130, 246, 0.08), 0 0 0 1px rgba(59, 130, 246, 0.05)';
        }
      }}
    >
      {/* Enhanced background gradient overlay */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: currentThemeName === 'dark'
          ? `linear-gradient(135deg, ${color}08 0%, rgba(147, 51, 234, 0.03) 100%)`
          : currentThemeName === 'beige'
          ? `linear-gradient(135deg, ${color}05 0%, rgba(160, 82, 45, 0.02) 100%)`
          : `linear-gradient(135deg, ${color}03 0%, rgba(147, 51, 234, 0.01) 100%)`,
        pointerEvents: 'none'
      }} />
      
      {/* Animated border glow */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: 20,
        background: `linear-gradient(45deg, ${color}20, transparent, ${color}20)`,
        opacity: 0,
        transition: 'opacity 0.3s ease',
        pointerEvents: 'none'
      }} />
      
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        marginBottom: 20, 
        position: 'relative', 
        zIndex: 1 
      }}>
        <div style={{
          width: 56,
          height: 56,
          borderRadius: 16,
          background: gradient,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          boxShadow: currentThemeName === 'dark'
            ? `0 4px 16px ${color}40`
            : currentThemeName === 'beige'
            ? `0 4px 16px ${color}30`
            : `0 4px 16px ${color}25`,
          transition: 'all 0.3s ease'
        }}>
          <Icon size={28} />
        </div>
      </div>
      
      <div style={{ 
        fontSize: 36, 
        fontWeight: 700, 
        color: currentThemeName === 'dark' ? 'white' : theme.text, 
        marginBottom: 8,
        position: 'relative',
        zIndex: 1,
        lineHeight: 1
      }}>
        {value.toLocaleString()}
      </div>
      
      <div style={{ 
        fontSize: 16, 
        color: currentThemeName === 'dark' ? '#e5e7eb' : theme.textSecondary, 
        marginBottom: 6,
        fontWeight: 600,
        position: 'relative',
        zIndex: 1
      }}>
        {title}
      </div>
      
      {description && (
        <div style={{ 
          fontSize: 12, 
          color: currentThemeName === 'dark' ? '#6b7280' : theme.textTertiary,
          position: 'relative',
          zIndex: 1,
          opacity: 0.7
        }}>
          {description}
        </div>
      )}
    </div>
  );
}

export default UserStats; 